import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CIVS } from '../../gameplay/civilisations';
import { Cat, KINDS } from '../../gameplay/registry';
import { C, type ResourceModel } from '../../gameplay/types';
import { HEIGHT_SCALE } from '../../world/maps/mapTypes';
import type { RTSCamera } from '../camera/rtsCamera';
import { FX_ONE } from '../simulation/fixed';
import { MAX_PROJECTILES, UState, type SimEvent, type World } from '../simulation/world';
import { AnimMode, AnimatedInstances, createAnimatedMaterial, setMaterialSky } from './animatedMaterial';
import { fowUniforms } from './fowShader';
import { WALL_E, WALL_N, WALL_S, WALL_W, ageTier, buildBuildingGeometry, buildRubble, buildScaffold, type BuildingVisual } from './models/buildingModels';
import { buildGrassTuft, buildProjectile, buildResourceGeometry, resourceVariants } from './models/natureModels';
import { buildUnitGeometry, civSkin, unitScale } from './models/unitModels';
import { ModelService } from './models/modelService';
import { captureProgress } from '../simulation/systems/capture';
import { ParticleSystem } from './particles';
import type { GraphicsSettings } from './settings';
import { LIGHTING, buildSky } from './sky';
import { HeightSampler, buildTerrain } from './terrain';
import { buildWater } from './water';

const INV = 1 / FX_ONE;
const WHITE = new THREE.Color(0xffffff);
/** Height of the water surface (see water.ts); markers over water sit on it, not on the seabed. */
const WATER_Y = -0.12;

interface Corpse {
  kind: number;
  civ: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  team: THREE.Color;
  t: number;
}

interface Rubble {
  x: number;
  y: number;
  z: number;
  scale: number;
  t: number;
}

interface Collapse {
  kind: number;
  owner: number;
  tier: number;
  x: number;
  z: number;
  t: number;
}

/** An animal model lying on its side: rolled 90 degrees about its length and rested on the ground. */
function fallenGeometry(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = src.clone();
  const roll = new THREE.Matrix4().makeRotationZ(Math.PI / 2);
  g.applyMatrix4(roll);
  const pivot = g.getAttribute('pivot') as THREE.BufferAttribute | undefined;
  if (pivot) {
    const v = new THREE.Vector3();
    for (let i = 0; i < pivot.count; i++) {
      v.fromBufferAttribute(pivot, i).applyMatrix4(roll);
      pivot.setXYZ(i, v.x, v.y, v.z);
    }
  }
  // No idle animation while lying down.
  const part = g.getAttribute('part') as THREE.BufferAttribute | undefined;
  if (part) for (let i = 0; i < part.count; i++) part.setX(i, 0);
  g.computeBoundingBox();
  g.translate(0, -g.boundingBox!.min.y + 0.02, 0);
  return g;
}

export interface Remembered {
  kind: number;
  owner: number;
  x: number;
  z: number;
  built: boolean;
  prog: number;
  tier: number;
  /** Whose architecture it shows (the builder of a captured building). */
  style?: number;
}

export interface RenderStats {
  drawCalls: number;
  triangles: number;
  renderMs: number;
  geometries: number;
  textures: number;
  /** Render passes per frame (shadow map + main scene) and the effects in use, for the F2 overlay. */
  passes: number;
  effects: string;
}

/**
 * Renders the simulation world. Reads simulation state each frame, never
 * mutates it. All repeated objects use GPU instancing keyed by model.
 */
export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly particles: ParticleSystem;
  readonly heights: HeightSampler;
  private sun: THREE.DirectionalLight;
  private hemi: THREE.HemisphereLight;
  private fowTex: THREE.DataTexture;
  private fowData: Uint8Array;
  // Units: glossy metal parts and a faint silhouette rim. Buildings and nature: contact shading
  // where walls and trunks meet the ground. All in the one lighting pass.
  private unitMat = createAnimatedMaterial({ roughness: 0.75, metals: true, groundAO: { height: 0.16, min: 0.72 }, rim: 0.1 });
  private buildMat = createAnimatedMaterial({ roughness: 0.85, surfaces: true, groundAO: { height: 0.45, min: 0.6 } });
  private natureMat = createAnimatedMaterial({ roughness: 0.9, groundAO: { height: 0.35, min: 0.62 } });
  private unitLayers = new Map<string, AnimatedInstances>();
  private buildingLayers = new Map<string, { inst: AnimatedInstances; vis: BuildingVisual; roof?: THREE.Vector3[] }>();
  private resourceLayers = new Map<string, AnimatedInstances>();
  private scaffoldLayers = new Map<number, AnimatedInstances>();
  private rubbleLayer: AnimatedInstances;
  private grass: AnimatedInstances | null = null;
  private projLayers = new Map<string, THREE.InstancedMesh>();
  private ringMesh: THREE.InstancedMesh;
  private barMesh: THREE.InstancedMesh;
  private barAttr: THREE.InstancedBufferAttribute;
  private ghost: THREE.Mesh | null = null;
  private ghostKey = '';
  /** Builds building models on background threads ahead of need (see prefetchModels). */
  private models = new ModelService();
  private prefetched = new Set<string>();
  private prefetchTimer = 0;
  /** Placement preview: lit (so the building's shape reads) and lightly self-lit in green or red. */
  private ghostMat = new THREE.MeshStandardMaterial({ color: 0x40ff60, emissive: 0x40ff60, emissiveIntensity: 0.35, roughness: 0.6, transparent: true, opacity: 0.55, depthWrite: false });
  /** Soft contact shadows under units and buildings (settings.contactShadows). */
  private blobMesh: THREE.InstancedMesh | null = null;
  private blobShape: THREE.InstancedBufferAttribute | null = null;
  private blobs = 0;
  private hiColor = new THREE.Color();
  private footprint: THREE.Mesh;
  private rally: THREE.Mesh;
  private yawSmooth = new Float32Array(8192);
  private yawInit = new Uint8Array(8192);
  private attackSeen = new Int32Array(8192).fill(-1);
  private attackTime = new Float32Array(8192);
  private corpses: Corpse[] = [];
  /** Per-unit eased display offset towards the resource being worked (see unit rendering). */
  private workOffX = new Float32Array(8192);
  private workOffZ = new Float32Array(8192);
  private tmpV1 = new THREE.Vector3();
  private tmpV2 = new THREE.Vector3();
  private tmpV3 = new THREE.Vector3();
  private tmpV4 = new THREE.Vector3();
  private fallenSeen = new Set<number>();
  /** Fish leaping out of the water near fish shoals (purely visual). */
  private leaps: { x: number; z: number; dx: number; dz: number; t: number; dur: number; splashed: boolean }[] = [];
  private leapMesh: THREE.InstancedMesh;
  private rubble: Rubble[] = [];
  private collapsing: Collapse[] = [];
  /** Enemy buildings the viewer has seen, as last seen. Nothing else of the enemy's is drawn outside vision. */
  readonly memory = new Map<number, Remembered>();
  private teamColors: THREE.Color[] = [];
  private tmpColor = new THREE.Color();
  private smokeTimer = 0;
  private fireTimer = 0;
  private time = 0;
  /** Local viewing player (fog perspective); -1 shows everything. */
  viewer = 0;
  revealAll = false;
  selection = new Set<number>();
  hovered = -1;
  stats: RenderStats = { drawCalls: 0, triangles: 0, renderMs: 0, geometries: 0, textures: 0, passes: 1, effects: '' };
  private pointMeshes: THREE.Group[] = [];
  /** Per-slot screen-projected positions computed during the last frame for picking. */
  readonly visibleSlots: number[] = [];

  constructor(
    readonly canvas: HTMLCanvasElement,
    readonly world: World,
    readonly cam: RTSCamera,
    readonly settings: GraphicsSettings,
  ) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: settings.antialias, powerPreference: 'high-performance', stencil: false });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = settings.shadows > 0;
    this.renderer.shadowMap.type = settings.shadows >= 2 ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
    this.applyPixelRatio();

    const map = world.map;
    this.heights = new HeightSampler(map);
    cam.heightAt = (x, z) => this.heights.at(x, z);
    const L = LIGHTING[settings.timeOfDay];
    this.scene.background = new THREE.Color(L.skyHorizon);
    this.scene.fog = new THREE.Fog(L.fog, 70, 220);
    this.scene.add(buildSky(L));
    this.hemi = new THREE.HemisphereLight(L.skyHorizon, L.ground, L.hemi);
    this.scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight(L.sun, L.sunIntensity);
    this.sun.position.copy(L.sunDir).multiplyScalar(60);
    this.sun.castShadow = settings.shadows > 0;
    const sm = [0, 1024, 2048, 4096][settings.shadows];
    this.sun.shadow.mapSize.set(sm || 512, sm || 512);
    const sc = this.sun.shadow.camera;
    sc.left = -38;
    sc.right = 38;
    sc.top = 38;
    sc.bottom = -38;
    sc.near = 1;
    sc.far = 160;
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.03;
    this.scene.add(this.sun, this.sun.target);
    // Metal reflections and the silhouette rim take the colours of this time of day's sky.
    setMaterialSky(this.unitMat.material, L);
    if (settings.contactShadows) this.buildBlobs();

    // Fog of war texture.
    this.fowData = new Uint8Array(map.size * map.size);
    this.fowTex = new THREE.DataTexture(this.fowData, map.size, map.size, THREE.RedFormat, THREE.UnsignedByteType);
    this.fowTex.magFilter = THREE.LinearFilter;
    this.fowTex.minFilter = THREE.LinearFilter;
    this.fowTex.needsUpdate = true;
    fowUniforms.uFowTex.value = this.fowTex;
    fowUniforms.uFowSize.value = map.size;

    this.scene.add(buildTerrain(map, settings.terrainDetail));
    const water = buildWater(map, settings.water, L.sunDir);
    if (water) this.scene.add(water);

    this.particles = new ParticleSystem(settings.particles >= 1 ? 4096 : 2048);
    this.particles.density = settings.particles;
    this.scene.add(this.particles.group);

    // Selection rings.
    // A crisp band with a soft glow inside it that breathes slowly (selected, hovered and targeted
    // things), coloured per instance.
    const ringGeo = new THREE.RingGeometry(0.62, 1.04, 40, 1);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: { uTime: fowUniforms.uTime },
      vertexShader: `
        varying vec3 vCol; varying float vR;
        void main(){
          vCol = vec3(1.0);
          #ifdef USE_INSTANCING_COLOR
            vCol = instanceColor;
          #endif
          vR = length(position.xz);
          gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: `
        uniform float uTime; varying vec3 vCol; varying float vR;
        void main(){
          float band = smoothstep(0.84, 0.9, vR) * (1.0 - smoothstep(0.98, 1.04, vR));
          float glow = smoothstep(0.7, 0.9, vR) * (1.0 - step(0.9, vR)) * (0.1 + 0.04 * sin(uTime * 4.0));
          gl_FragColor = vec4(vCol * (1.0 + 0.25 * band), max(band * 0.95, glow));
          #include <colorspace_fragment>
        }`,
    });
    this.ringMesh = new THREE.InstancedMesh(ringGeo, ringMat, 512);
    this.ringMesh.count = 0;
    this.ringMesh.frustumCulled = false;
    this.ringMesh.renderOrder = 3;
    this.ringMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(512 * 3), 3);
    this.scene.add(this.ringMesh);

    // Health bars (billboarded in shader).
    const barGeo = new THREE.PlaneGeometry(1, 1);
    this.barAttr = new THREE.InstancedBufferAttribute(new Float32Array(1024 * 4), 4);
    barGeo.setAttribute('aBar', this.barAttr);
    const barMat = new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      vertexShader: `
        attribute vec4 aBar; // hp fraction, r, g, b
        varying vec2 vUv; varying vec4 vBar;
        void main(){
          vUv = uv; vBar = aBar;
          vec4 c = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          float w = instanceMatrix[0][0];
          c.xy += position.xy * vec2(w, 0.09);
          gl_Position = projectionMatrix * c;
        }`,
      fragmentShader: `
        varying vec2 vUv; varying vec4 vBar;
        void main(){
          if (vUv.x < 0.04 || vUv.x > 0.96 || vUv.y < 0.15 || vUv.y > 0.85) { gl_FragColor = vec4(0.05,0.05,0.05,0.85); return; }
          vec3 c = vUv.x < vBar.x ? vBar.yzw : vec3(0.25, 0.08, 0.06);
          gl_FragColor = vec4(c, 0.95);
        }`,
    });
    this.barMesh = new THREE.InstancedMesh(barGeo, barMat, 1024);
    this.barMesh.count = 0;
    this.barMesh.frustumCulled = false;
    this.barMesh.renderOrder = 10;
    this.scene.add(this.barMesh);

    const fpGeo = new THREE.PlaneGeometry(1, 1);
    fpGeo.rotateX(-Math.PI / 2);
    this.footprint = new THREE.Mesh(fpGeo, new THREE.MeshBasicMaterial({ color: 0x40ff60, transparent: true, opacity: 0.25, depthWrite: false }));
    this.footprint.visible = false;
    this.footprint.renderOrder = 4;
    this.scene.add(this.footprint);

    const flag = new THREE.Group();
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.9), new THREE.MeshStandardMaterial({ color: 0x5a4028 }));
    pole.position.y = 0.45;
    const cloth = new THREE.Mesh(new THREE.PlaneGeometry(0.35, 0.22), new THREE.MeshStandardMaterial({ color: 0xffffff, side: THREE.DoubleSide }));
    cloth.position.set(0.18, 0.78, 0);
    flag.add(pole, cloth);
    this.rally = new THREE.Mesh();
    this.rally.add(flag);
    this.rally.visible = false;
    this.scene.add(this.rally);

    {
      // A small silver fish: body plus tail fin, pointing along +z.
      const body = new THREE.SphereGeometry(0.09, 8, 6).scale(0.55, 0.7, 1.6);
      const tail = new THREE.ConeGeometry(0.07, 0.12, 4).rotateX(-Math.PI / 2).translate(0, 0, -0.19);
      const geo = mergeGeometries([body.toNonIndexed(), tail.toNonIndexed()]);
      this.leapMesh = new THREE.InstancedMesh(geo, new THREE.MeshStandardMaterial({ color: 0xb8c8d0, metalness: 0.6, roughness: 0.35 }), 16);
      this.leapMesh.count = 0;
      this.leapMesh.frustumCulled = false;
      this.scene.add(this.leapMesh);
    }
    this.rubbleLayer = new AnimatedInstances(buildRubble(), this.buildMat.material, this.buildMat.depth, 32);
    this.scene.add(this.rubbleLayer.mesh);

    if (settings.vegetation > 0) this.buildGrass(settings.vegetation);
    // Bold, saturated team colours so the team-coloured parts read clearly at a distance.
    this.teamColors = world.players.map((p) => {
      const c = new THREE.Color(p.color);
      const hsl = { h: 0, s: 0, l: 0 };
      c.getHSL(hsl);
      return c.setHSL(hsl.h, Math.min(1, hsl.s * 1.15 + 0.1), Math.min(0.56, Math.max(0.42, hsl.l)));
    });
    this.updateFog(true);
  }

  /**
   * Contact shadows: a soft dark patch on the ground under each visible unit (round) and building
   * (rounded square), drawn as one instanced mesh before the selection rings. It grounds models
   * where the shadow map is coarse or off; it is not a screen-space pass, so it costs one draw call
   * and a few small transparent quads, and it cannot smear onto cut-away or transparent geometry.
   */
  private buildBlobs(): void {
    const geo = new THREE.PlaneGeometry(2, 2);
    geo.rotateX(-Math.PI / 2);
    this.blobShape = new THREE.InstancedBufferAttribute(new Float32Array(2048 * 2), 2);
    geo.setAttribute('aShape', this.blobShape);
    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      vertexShader: `
        attribute vec2 aShape; // x: 0 round, 1 square; y: strength
        varying vec2 vP; varying vec2 vShape;
        void main(){
          vP = position.xz; vShape = aShape;
          gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: `
        varying vec2 vP; varying vec2 vShape;
        void main(){
          vec2 q = abs(vP);
          float d = vShape.x > 0.5 ? length(max(q - 0.55, 0.0)) / 0.45 : length(vP);
          float a = (1.0 - smoothstep(0.25, 1.0, d)) * vShape.y;
          if (a < 0.004) discard;
          gl_FragColor = vec4(0.0, 0.0, 0.0, a);
        }`,
    });
    this.blobMesh = new THREE.InstancedMesh(geo, mat, 2048);
    this.blobMesh.count = 0;
    this.blobMesh.frustumCulled = false;
    this.blobMesh.renderOrder = 2;
    this.scene.add(this.blobMesh);
  }

  private blobMtx = new THREE.Matrix4();
  private pushBlob(x: number, y: number, z: number, sx: number, sz: number, square: boolean, strength: number): void {
    if (!this.blobMesh || this.blobs >= 2048) return;
    this.blobMtx.makeScale(sx, 1, sz).setPosition(x, y + 0.03, z);
    this.blobMesh.setMatrixAt(this.blobs, this.blobMtx);
    const a = this.blobShape!.array as Float32Array;
    a[this.blobs * 2] = square ? 1 : 0;
    a[this.blobs * 2 + 1] = strength;
    this.blobs++;
  }

  /** Extra resolution multiplier controlled by dynamic resolution (0.65..1). */
  dynamicScale = 1;

  applyPixelRatio(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, this.settings.maxPixelRatio) * this.settings.renderScale * this.dynamicScale;
    this.renderer.setPixelRatio(Math.max(0.5, dpr));
  }

  /** Adjust dynamic resolution; returns true if the pixel ratio changed. */
  setDynamicScale(v: number): boolean {
    const n = Math.min(1, Math.max(0.65, Math.round(v * 20) / 20));
    if (n === this.dynamicScale) return false;
    this.dynamicScale = n;
    const size = this.renderer.getSize(new THREE.Vector2());
    this.applyPixelRatio();
    this.renderer.setSize(size.x, size.y, false);
    this.particles.setPixelScale(size.y * this.renderer.getPixelRatio() * 0.9);
    return true;
  }

  resize(w: number, h: number): void {
    this.renderer.setSize(w, h, false);
    this.cam.camera.aspect = w / Math.max(1, h);
    this.cam.camera.updateProjectionMatrix();
    this.particles.setPixelScale(h * this.renderer.getPixelRatio() * 0.9);
  }

  private buildGrass(density: number): void {
    const map = this.world.map;
    const geo = buildGrassTuft();
    this.grass = new AnimatedInstances(geo, this.natureMat.material, null, 1024, false);
    this.grass.begin();
    const col = new THREE.Color(1, 1, 1);
    let seed = map.seed;
    const rnd = () => ((seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0) / 4294967296);
    const n = Math.round(map.size * map.size * 0.35 * density);
    for (let i = 0; i < n; i++) {
      const x = rnd() * map.size;
      const z = rnd() * map.size;
      const t = map.terrain[Math.floor(z) * map.size + Math.floor(x)];
      if (t !== 0 && t !== 7) continue;
      this.grass.push(x, this.heights.at(x, z), z, rnd() * 6.28, 0.7 + rnd() * 0.8, col, rnd() * 10, AnimMode.Static, 0, 0);
    }
    this.grass.end();
    this.scene.add(this.grass.mesh);
  }

  // ---------- Layer lookup ----------
  private unitLayer(kind: number, civ: number): AnimatedInstances {
    const key = `${kind}:${civ}`;
    let l = this.unitLayers.get(key);
    if (!l) {
      const u = KINDS[kind].unit!;
      const c = CIVS[civ];
      const skin = civSkin(c.key);
      l = new AnimatedInstances(buildUnitGeometry(u, { accent: c.uniform, skin }), this.unitMat.material, this.unitMat.depth, 32);
      this.unitLayers.set(key, l);
      this.scene.add(l.mesh);
    }
    return l;
  }

  /** Which neighbouring tiles hold the same player's wall pieces (see wallPiece in buildingModels). */
  wallVariant(tx: number, ty: number, owner: number, extra?: (x: number, y: number) => number): number {
    const w = this.world;
    let v = 0;
    for (const [bit, dx, dy] of [[WALL_N, 0, -1], [WALL_E, 1, 0], [WALL_S, 0, 1], [WALL_W, -1, 0]] as const) {
      const x = tx + dx;
      const y = ty + dy;
      if (x < 0 || y < 0 || x >= w.size || y >= w.size) continue;
      let k = -1;
      const oid = w.occupant[y * w.size + x];
      const o = oid >= 0 ? w.resolve(oid) : -1;
      if (o >= 0 && w.alive[o] && w.cat[o] === Cat.Building && w.owner[o] === owner && KINDS[w.kind[o]].building!.classes & C.Wall) k = w.kind[o];
      else if (extra) k = extra(x, y);
      if (k < 0) continue;
      v |= bit;
      const key = KINDS[k].key;
      if (key === 'stoneWall' || key === 'stonePillar') v |= bit << 4;
    }
    return v;
  }

  /**
   * The instanced layer drawing one building model. A model not yet made comes from the background
   * builder if it has finished it; if it is still being made, the same building's model from
   * another age stands in for a moment rather than freezing the game to build it here. Only when
   * there is nothing to stand in (or `exact` is asked) is the model built on the spot.
   */
  private buildingLayer(kind: number, civ: number, tier: number, variant = 0, exact = false): { inst: AnimatedInstances; vis: BuildingVisual; roof?: THREE.Vector3[] } {
    const key = `${kind}:${civ}:${tier}:${variant}`;
    let l = this.buildingLayers.get(key);
    if (l) return l;
    let vis = this.models.take(key);
    if (!vis && !exact && this.models.available) {
      const stand = this.nearestLayer(kind, civ, tier, variant);
      if (stand) {
        this.models.request({ key, kind, civ, tier, variant });
        return stand;
      }
    }
    if (!vis) {
      vis = buildBuildingGeometry(KINDS[kind].building!, CIVS[civ], tier, variant);
      this.models.forget(key);
    }
    l = { inst: new AnimatedInstances(vis.geometry, this.buildMat.material, this.buildMat.depth, 16), vis };
    this.buildingLayers.set(key, l);
    this.scene.add(l.inst.mesh);
    return l;
  }

  /** The same building's layer from the nearest other age, earlier ages first. */
  private nearestLayer(kind: number, civ: number, tier: number, variant: number) {
    for (let d = 1; d <= 4; d++) {
      for (const t of [tier - d, tier + d]) {
        const l = t >= 0 && t <= 4 ? this.buildingLayers.get(`${kind}:${civ}:${t}:${variant}`) : undefined;
        if (l) return l;
      }
    }
    return null;
  }

  /**
   * Asks the background builder for models before they are needed: for every player, the next
   * age's models of the building types they have (ready for when they advance); for the viewer,
   * every building type at their current age (the placement preview and first new buildings).
   */
  private prefetchModels(dt: number): void {
    if (!this.models.available || (this.prefetchTimer -= dt) > 0) return;
    this.prefetchTimer = 1;
    const w = this.world;
    const want = (kind: number, civ: number, tier: number) => {
      if (tier > 4) return;
      const key = `${kind}:${civ}:${tier}:0`;
      if (this.prefetched.has(key) || this.buildingLayers.has(key)) return;
      this.prefetched.add(key);
      this.models.request({ key, kind, civ, tier, variant: 0 });
    };
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.cat[s] !== Cat.Building || w.owner[s] < 0) continue;
      const b = KINDS[w.kind[s]].building!;
      if (b.classes & C.Wall) continue;
      const p = w.players[w.owner[s]];
      want(w.kind[s], p.civ, ageTier(p.age) + 1);
    }
    if (this.viewer >= 0) {
      const p = w.players[this.viewer];
      for (const k of KINDS) if (k.building && !(k.building.classes & C.Wall) && k.building.buildable !== false) want(k.index, p.civ, ageTier(p.age));
    }
  }

  /**
   * Points on a building's roof (its highest surface over a grid across the footprint), worked out
   * once per model by casting down onto it. Damage fires burn here, so they always sit on top.
   */
  private roofPoints(l: { vis: BuildingVisual; roof?: THREE.Vector3[] }, size: number): THREE.Vector3[] {
    if (l.roof) return l.roof;
    const mesh = new THREE.Mesh(l.vis.geometry, new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }));
    const ray = new THREE.Raycaster();
    const down = new THREE.Vector3(0, -1, 0);
    const n = 6;
    const half = size * 0.4 * l.vis.scale;
    const pts: THREE.Vector3[] = [];
    // Cell centres only (never the exact middle, where banner poles stand).
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const px = ((i + 0.5) / n - 0.5) * 2 * half;
        const pz = ((j + 0.5) / n - 0.5) * 2 * half;
        ray.set(new THREE.Vector3(px, 50, pz), down);
        const hit = ray.intersectObject(mesh, false)[0];
        if (hit && hit.point.y > 0.25) pts.push(hit.point.clone());
      }
    }
    (mesh.material as THREE.Material).dispose();
    if (!pts.length) pts.push(new THREE.Vector3(0, l.vis.height * 0.8, 0));
    l.roof = pts;
    return pts;
  }

  private resourceLayer(model: ResourceModel, variant: number, fallen = false): AnimatedInstances {
    const key = `${model}:${variant}${fallen ? ':f' : ''}`;
    let l = this.resourceLayers.get(key);
    if (!l) {
      const geo = fallen ? fallenGeometry(buildResourceGeometry(model, variant)) : buildResourceGeometry(model, variant);
      l = new AnimatedInstances(geo, this.natureMat.material, this.natureMat.depth, 256, this.settings.shadows >= 1);
      this.resourceLayers.set(key, l);
      this.scene.add(l.mesh);
    }
    return l;
  }

  private scaffold(size: number): AnimatedInstances {
    let l = this.scaffoldLayers.get(size);
    if (!l) {
      l = new AnimatedInstances(buildScaffold(size, 1.4), this.buildMat.material, null, 8, false);
      this.scaffoldLayers.set(size, l);
      this.scene.add(l.mesh);
    }
    return l;
  }

  private projLayer(kind: string): THREE.InstancedMesh {
    let m = this.projLayers.get(kind);
    if (!m) {
      m = new THREE.InstancedMesh(buildProjectile(kind), new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, emissive: kind === 'bullet' ? 0xffd070 : 0x000000, emissiveIntensity: kind === 'bullet' ? 1.5 : 0 }), 512);
      m.castShadow = true;
      m.count = 0;
      m.frustumCulled = false;
      this.projLayers.set(kind, m);
      this.scene.add(m);
    }
    return m;
  }

  // ---------- Fog ----------
  updateFog(force = false): void {
    const w = this.world;
    const vis = this.viewer >= 0 ? w.visibility[this.viewer] : null;
    const d = this.fowData;
    if (!vis || this.revealAll) {
      if (force || d[0] !== 255) d.fill(255);
    } else {
      for (let i = 0; i < d.length; i++) d[i] = vis[i] === 2 ? 255 : vis[i] === 1 ? 128 : 0;
      this.rememberSeenBuildings(vis);
    }
    this.fowTex.needsUpdate = true;
  }

  /** Record enemy buildings in current vision, on or off screen, so they persist as last seen. */
  private rememberSeenBuildings(vis: Uint8Array): void {
    const w = this.world;
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.cat[s] !== Cat.Building) continue;
      const owner = w.owner[s];
      if (owner === this.viewer) continue;
      if (vis[(w.y[s] >> 10) * w.size + (w.x[s] >> 10)] !== 2) continue;
      const kind = w.kind[s];
      const p = w.players[owner];
      const prog = w.built[s] ? 1 : w.buildProg[s] / Math.max(1, p.stats[kind].buildTicks * 1000);
      this.memory.set(w.idOf(s), { kind, owner, x: w.x[s] * INV, z: w.y[s] * INV, built: !!w.built[s], prog, tier: ageTier(p.age) });
    }
  }

  private visAt(x: number, z: number): number {
    if (this.viewer < 0 || this.revealAll) return 2;
    const v = this.world.visibility[this.viewer];
    if (!v) return 2;
    const tx = Math.floor(x);
    const tz = Math.floor(z);
    if (tx < 0 || tz < 0 || tx >= this.world.size || tz >= this.world.size) return 0;
    return v[tz * this.world.size + tx];
  }

  // ---------- Events ----------
  onEvents(events: SimEvent[]): void {
    const w = this.world;
    for (const e of events) {
      switch (e.e) {
        case 'death': {
          const x = e.x * INV;
          const z = e.y * INV;
          if (this.visAt(x, z) !== 2) break;
          const y = this.heights.at(x, z);
          if (e.building) {
            const size = KINDS[e.kind].building!.size;
            this.rubble.push({ x, y, z, scale: size * 0.9, t: this.time });
            // The building sinks into its own rubble (construction clipping in reverse).
            if (e.owner >= 0) this.collapsing.push({ kind: e.kind, owner: e.owner, tier: ageTier(w.players[e.owner].age), x, z, t: this.time });
            this.particles.burst('dust', x, y + 0.3, z, 30 + size * 10, 1.4 + size * 0.3, 0.6);
            this.particles.burst('smoke', x, y + 0.5, z, 12 + size * 4, 0.6, 0.6);
            this.particles.burst('chips', x, y + 0.5, z, 20, 2, 1.5);
          } else if (e.owner >= 0) {
            this.corpses.push({ kind: e.kind, civ: w.players[e.owner].civ, x, y, z, yaw: Math.atan2(e.fx, e.fy), team: this.teamColors[e.owner], t: this.time });
            this.particles.burst('dust', x, y + 0.1, z, 4, 0.5, 0.3);
          }
          break;
        }
        case 'hit': {
          const x = e.x * INV;
          const z = e.y * INV;
          if (this.visAt(x, z) !== 2) break;
          const y = this.heights.at(x, z);
          if (e.building) this.particles.burst('chips', x, y + 0.6, z, e.splash ? 3 : 2, 1.2, 1.2);
          else this.particles.burst(e.splash ? 'dust' : 'blood', x, y + 0.35, z, e.splash ? 3 : 2, 0.8, 1);
          if (e.dmg >= 30) {
            this.particles.burst('flash', x, y + 0.3, z, 1, 0, 0);
            this.particles.burst('dust', x, y + 0.2, z, 10, 1.6, 0.8);
            this.particles.burst('spark', x, y + 0.3, z, 8, 2.5, 1.5);
          }
          break;
        }
        case 'attack': {
          const atk = KINDS[e.kind].unit?.attack;
          const x = e.x * INV;
          const z = e.y * INV;
          if (atk?.projectile && (atk.projectile === 'bullet' || atk.projectile === 'ball' || atk.projectile === 'shell') && this.visAt(x, z) === 2) {
            const y = this.heights.at(x, z);
            this.particles.burst('muzzle', x, y + 0.45, z, atk.projectile === 'bullet' ? 3 : 8, 0.4, 0.4);
            this.particles.burst('flash', x, y + 0.4, z, 1, 0, 0);
          }
          break;
        }
        case 'built': {
          const s = w.resolve(e.id);
          if (s >= 0) {
            const x = w.x[s] * INV;
            const z = w.y[s] * INV;
            this.particles.burst('dust', x, this.heights.at(x, z) + 0.1, z, 20, 1.5, 0.3);
          }
          break;
        }
        case 'heal': {
          const x = e.x * INV;
          const z = e.y * INV;
          if (this.visAt(x, z) === 2) this.particles.burst('heal', x, this.heights.at(x, z) + 0.4, z, 3, 0.25, 0.6);
          break;
        }
        case 'gatherTick': {
          const x = e.x * INV;
          const z = e.y * INV;
          if (this.visAt(x, z) === 2 && Math.random() < 0.5) this.particles.burst(e.res === 1 ? 'chips' : 'dust', x, this.heights.at(x, z) + 0.3, z, 2, 0.6, 0.8);
          break;
        }
        default:
          break;
      }
    }
  }

  // ---------- Frame ----------
  /** Render one frame. alpha interpolates between previous and current simulation tick. */
  render(dt: number, alpha: number, placement: { kind: number; tx: number; ty: number; valid: boolean } | null, rallyOf: number): void {
    const t0 = performance.now();
    this.time += dt;
    this.prefetchModels(dt);
    fowUniforms.uTime.value = this.time;
    const w = this.world;
    const cam = this.cam.camera;
    // Sun follows the camera for tight shadow bounds.
    const L = LIGHTING[this.settings.timeOfDay];
    const sc = this.sun.shadow.camera;
    // Shadow area grows with zoom in steps, and its centre snaps to the shadow-map texel grid in
    // light space, so shadow edges stay still instead of shimmering as the camera moves.
    const ext = Math.min(60, Math.ceil((18 + this.cam.distance * 0.9) / 4) * 4);
    {
      const texel = (2 * ext) / this.sun.shadow.mapSize.x;
      const dir = this.tmpV1.copy(L.sunDir).normalize();
      const right = this.tmpV2.set(0, 1, 0).cross(dir).normalize();
      const up = this.tmpV3.copy(dir).cross(right).normalize();
      const t = this.cam.target;
      const r = Math.round(t.dot(right) / texel) * texel;
      const u = Math.round(t.dot(up) / texel) * texel;
      const f = t.dot(dir);
      const snapped = this.tmpV4.copy(right).multiplyScalar(r).addScaledVector(up, u).addScaledVector(dir, f);
      this.sun.target.position.copy(snapped);
      this.sun.position.copy(snapped).addScaledVector(dir, 70);
    }
    if (sc.right !== ext) {
      sc.left = -ext;
      sc.right = ext;
      sc.top = ext;
      sc.bottom = -ext;
      sc.updateProjectionMatrix();
    }

    for (const l of this.unitLayers.values()) l.begin();
    for (const l of this.buildingLayers.values()) l.inst.begin();
    for (const l of this.resourceLayers.values()) l.begin();
    for (const l of this.scaffoldLayers.values()) l.begin();
    this.rubbleLayer.begin();

    let rings = 0;
    let bars = 0;
    this.blobs = 0;
    const ringMtx = new THREE.Matrix4();
    const barMtx = new THREE.Matrix4();
    const barArr = this.barAttr.array as Float32Array;
    const ringCol = this.tmpColor;
    this.visibleSlots.length = 0;
    const smokeNow = (this.smokeTimer += dt) > 0.35;
    if (smokeNow) this.smokeTimer = 0;
    // Flames are fed more often than smoke so they burn steadily rather than puffing.
    const fireNow = (this.fireTimer += dt) > 0.12;
    if (fireNow) this.fireTimer = 0;
    const frustum = new THREE.Frustum().setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse));
    const sphere = new THREE.Sphere(new THREE.Vector3(), 3);
    const viewer = this.viewer;

    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s]) continue;
      const cat = w.cat[s];
      if (cat === Cat.Held) continue;
      const kind = w.kind[s];
      const owner = w.owner[s];
      const x = (w.px[s] + (w.x[s] - w.px[s]) * alpha) * INV;
      const z = (w.py[s] + (w.y[s] - w.py[s]) * alpha) * INV;
      const vis = this.visAt(x, z);
      const own = owner === viewer || viewer < 0;
      if (cat === Cat.Unit) {
        if (!own && vis !== 2) continue;
      } else if (vis === 0) continue;
      sphere.center.set(x, this.heights.at(x, z), z);
      sphere.radius = cat === Cat.Building ? 4 : 2;
      if (!frustum.intersectsSphere(sphere)) continue;
      const y = sphere.center.y;
      this.visibleSlots.push(s);
      const id = w.idOf(s);
      const selected = this.selection.has(id);

      if (cat === Cat.Unit) {
        const u = KINDS[kind].unit!;
        // Smooth facing.
        const targetYaw = Math.atan2(w.fdx[s], w.fdy[s]);
        if (!this.yawInit[s] || this.attackSeen[s] === -2) {
          this.yawSmooth[s] = targetYaw;
          this.yawInit[s] = 1;
        }
        let dy = targetYaw - this.yawSmooth[s];
        dy = Math.atan2(Math.sin(dy), Math.cos(dy));
        this.yawSmooth[s] += dy * Math.min(1, dt * 10);
        let mode: number = AnimMode.Idle;
        const st = w.state[s];
        if (w.lastAttackTick[s] !== this.attackSeen[s]) {
          this.attackSeen[s] = w.lastAttackTick[s];
          this.attackTime[s] = this.time;
        }
        if (w.moving[s] && (w.x[s] !== w.px[s] || w.y[s] !== w.py[s])) mode = AnimMode.Walk;
        else if (st === UState.Attacking) mode = AnimMode.Attack;
        else if (st === UState.Gathering || st === UState.Building || st === UState.Healing) mode = AnimMode.Work;
        const team = owner >= 0 ? this.teamColors[owner] : this.tmpColor.set(0xffffff);
        const layer = this.unitLayer(kind, w.players[owner].civ);
        const naval = (u.classes & C.Ship) !== 0;
        // A working gatherer is drawn stepped in against its tree, rock or bush: the simulation keeps
        // units out of a resource's tile, which otherwise leaves a visible gap. Eased so it never pops.
        let ox = 0;
        let oz = 0;
        if (mode === AnimMode.Work && st === UState.Gathering) {
          const t = w.resolve(w.target[s]);
          const res = t >= 0 && w.cat[t] === Cat.Resource ? KINDS[w.kind[t]].resource! : null;
          if (res && res.model !== 'fish') {
            const tx = w.x[t] * INV - x;
            const tz = w.y[t] * INV - z;
            const d = Math.hypot(tx, tz);
            const step = d > 1e-3 ? Math.min(0.4, Math.max(0, d - res.radius - u.radius * 0.5)) : 0;
            if (step > 0) {
              ox = (tx / d) * step;
              oz = (tz / d) * step;
            }
          }
        }
        const ease = Math.min(1, dt * 6);
        this.workOffX[s] += (ox - this.workOffX[s]) * ease;
        this.workOffZ[s] += (oz - this.workOffZ[s]) * ease;
        const vx = x + this.workOffX[s];
        const vz = z + this.workOffZ[s];
        const wy = naval ? -0.16 + Math.sin(this.time * 1.7 + s) * 0.03 : this.workOffX[s] || this.workOffZ[s] ? this.heights.at(vx, vz) : y;
        layer.push(vx, wy, vz, this.yawSmooth[s], unitScale(u), team, (s * 0.37) % 6.28, naval && mode === AnimMode.Walk ? AnimMode.Walk : mode, this.attackTime[s], 1);
        // Selected: an edge light in a pale shade of the player's own colour (the hue stays readable);
        // an enemy under the cursor: red.
        if (selected || s === this.hovered) layer.highlight(own ? this.hiColor.copy(team).lerp(WHITE, 0.55) : this.hiColor.set(0xff5040), selected ? 0.32 : 0.2);
        if (!naval) {
          const br = u.radius * unitScale(u) * (u.classes & (C.Cavalry | C.Siege | C.Elephant) ? 2.6 : 2.2);
          this.pushBlob(vx, wy, vz, br, br, false, 0.42);
        }
        if (naval && mode === AnimMode.Walk && Math.random() < 0.15) this.particles.burst('splash', x - Math.sin(this.yawSmooth[s]) * 0.6, -0.1, z - Math.cos(this.yawSmooth[s]) * 0.6, 1, 0.3, 0.3);
        if (selected || s === this.hovered) {
          const r = u.radius * 1.6 + 0.12;
          // Drawn where the unit is shown, which includes the step in towards work.
          ringMtx.makeScale(r, 1, r).setPosition(vx, Math.max(naval ? y : wy, WATER_Y) + 0.04, vz);
          this.ringMesh.setMatrixAt(rings, ringMtx);
          this.ringMesh.setColorAt(rings, ringCol.set(own ? (selected ? 0x7cff7c : 0xc8ffc8) : 0xff6a5a));
          rings++;
        }
        if ((selected || w.hp[s] < w.maxHp[s]) && bars < 1024) {
          const bw = Math.max(0.5, u.radius * 2.6);
          barMtx.makeScale(bw, 1, 1).setPosition(vx, Math.max(naval ? y : wy, WATER_Y) + (u.model === 'elephant' ? 1.7 : u.classes & C.Cavalry ? 1.15 : u.classes & C.Siege ? 1.0 : 0.85), vz);
          this.barMesh.setMatrixAt(bars, barMtx);
          this.writeBar(barArr, bars, w.hp[s] / w.maxHp[s], owner);
          bars++;
        }
      } else if (cat === Cat.Building) {
        const b = KINDS[kind].building!;
        const p = w.players[owner];
        // A captured building keeps the architecture of the side that built it.
        const style = w.builtBy[s] ? w.builtBy[s] - 1 : owner;
        const tier = ageTier(w.players[style].age);
        const builtFrac = w.built[s] ? 1 : w.buildProg[s] / Math.max(1, p.stats[kind].buildTicks * 1000);
        if (!own && vis !== 2) {
          // Show the last-known state of enemy buildings outside vision.
          const m = this.memory.get(id);
          if (m) this.pushBuilding(m.kind, m.owner, m.x, m.z, m.tier, m.built ? 1 : m.prog, s, true, m.style);
          continue;
        }
        if (!own) this.memory.set(id, { kind, owner, x, z, built: !!w.built[s], prog: builtFrac, tier, style });
        const l = this.pushBuilding(kind, owner, x, z, tier, builtFrac, s, true, style);
        if (selected || s === this.hovered) l.inst.highlight(own ? this.hiColor.copy(this.teamColors[owner]).lerp(WHITE, 0.55) : this.hiColor.set(0xff5040), selected ? 0.24 : 0.15);
        if (!b.foodAmount && !(b.classes & C.Wall)) this.pushBlob(x, y, z, b.size * 0.62, b.size * 0.62, true, 0.34);
        if (!w.built[s] && !(b.classes & C.Wall)) {
          this.scaffold(b.size).push(x, y, z, 0, l.vis.scale, this.teamColors[owner], 0, AnimMode.Static, 0, 0);
        } else {
          // Damage: dark smoke rises from 50% health; from 30% fires burn on the roof at several fixed spots.
          const hpf = w.hp[s] / w.maxHp[s];
          // (A building being commandeered is not damaged: no smoke or fire. Nor is a wall still being
          // built: its health is low only because it is unfinished.)
          if ((smokeNow || fireNow) && hpf <= 0.5 && vis === 2 && w.built[s] && !w.captureBy[s] && !(b.classes & C.Wall && hpf > 0.3)) {
            const burning = hpf <= 0.3;
            const spots = burning ? 2 + Math.min(4, b.size) : 1 + (b.size >= 3 ? 1 : 0);
            const roof = this.roofPoints(l, b.size);
            for (let i = 0; i < spots; i++) {
              const h = Math.sin(s * 12.9898 + i * 78.233) * 43758.5453;
              const rp = roof[Math.floor((h - Math.floor(h)) * roof.length)];
              const ox = rp.x;
              const oz = rp.z;
              const top = y + rp.y + 0.05;
              if (burning && fireNow) {
                this.particles.burst('flame', x + ox, top, z + oz, 2, 0.12, 1);
                this.particles.burst('fire', x + ox, top + 0.1, z + oz, 2, 0.15, 1);
                if (this.settings.glow) this.particles.burst('glow', x + ox, top + 0.25, z + oz, 1, 0.1, 0.4);
                if (Math.random() < 0.1) this.particles.burst('spark', x + ox, top + 0.2, z + oz, 2, 0.6, 1.5);
              }
              if (smokeNow) this.particles.burst('soot', x + ox, top + (burning ? 0.4 : 0.1), z + oz, burning ? 2 : 1, 0.15, 0.55);
            }
          }
          if (smokeNow && vis === 2 && l.vis.smoke.length && Math.random() < 0.5) {
            const c = l.vis.smoke[0];
            this.particles.burst('smoke', x + c.x, y + c.y, z + c.z, 1, 0.1, 0.3);
          }
        }
        if (selected || s === this.hovered) {
          const r = b.size * 0.75;
          ringMtx.makeScale(r, 1, r).setPosition(x, Math.max(y, WATER_Y) + 0.06, z);
          this.ringMesh.setMatrixAt(rings, ringMtx);
          this.ringMesh.setColorAt(rings, ringCol.set(own ? 0x7cff7c : 0xff6a5a));
          rings++;
        }
        const capture = w.captureBy[s] ? captureProgress(w, s) : -1;
        if ((selected || w.hp[s] < w.maxHp[s] || capture >= 0) && bars < 1024 && (!b.foodAmount || capture >= 0)) {
          barMtx.makeScale(b.size * 0.7, 1, 1).setPosition(x, y + l.vis.height + 0.35, z);
          this.barMesh.setMatrixAt(bars, barMtx);
          // Being commandeered: the bar runs down in the owner's colour, then fills in the taker's.
          if (capture >= 0) this.writeBar(barArr, bars, w.hp[s] / w.maxHp[s], capture < 0.5 ? owner : w.captureBy[s] - 1, true);
          else this.writeBar(barArr, bars, w.built[s] ? w.hp[s] / w.maxHp[s] : builtFrac, owner);
          bars++;
        }
      } else {
        const r = KINDS[kind].resource!;
        const variants = resourceVariants(r.model);
        const v = s % variants;
        const amt = w.resAmt[s] / (r.amount * FX_ONE);
        const scale = r.model === 'gold' || r.model === 'stone' || r.model === 'berry' ? 0.6 + 0.4 * amt : 1;
        const isAnimal = r.model === 'deer' || r.model === 'sheep';
        if (r.model === 'fish' && vis === 2 && this.leaps.length < 16 && Math.random() < dt * 0.07) {
          const a = Math.random() * Math.PI * 2;
          this.leaps.push({ x: x + Math.cos(a) * 0.6, z: z + Math.sin(a) * 0.6, dx: Math.sin(a + 1.3), dz: Math.cos(a + 1.3), t: 0, dur: 0.7 + Math.random() * 0.3, splashed: false });
          this.particles.burst('splash', x + Math.cos(a) * 0.6, WATER_Y + 0.02, z + Math.sin(a) * 0.6, 4, 0.4, 0.4);
        }
        // Hunted and herded animals drop onto their side at the first strike, then are butchered.
        const fallen = isAnimal && w.resAmt[s] < r.amount * FX_ONE;
        if (fallen && !this.fallenSeen.has(id)) {
          this.fallenSeen.add(id);
          this.particles.burst('dust', x, y + 0.1, z, 6, 0.4, 0.3);
        }
        const rl = this.resourceLayer(r.model, v, fallen);
        rl.push(x, r.model === 'fish' ? 0 : y, z, (s * 2.399) % 6.28, scale * (isAnimal ? 1 : 0.9 + ((s * 7) % 5) * 0.05), this.tmpColor.set(0xffffff), (s * 0.71) % 6.28, AnimMode.Static, 0, 0);
        if (selected || s === this.hovered) rl.highlight(this.hiColor.set(0xffe080), selected ? 0.3 : 0.2);
        if (selected || s === this.hovered) {
          ringMtx.makeScale(0.6, 1, 0.6).setPosition(x, Math.max(y, WATER_Y) + 0.05, z);
          this.ringMesh.setMatrixAt(rings, ringMtx);
          this.ringMesh.setColorAt(rings, ringCol.set(0xffe080));
          rings++;
        }
      }
      if (rings >= 511) rings = 511;
    }

    // Remembered buildings that were destroyed out of sight still show until re-scouted.
    for (const [id, m] of this.memory) {
      if (w.resolve(id) >= 0) continue;
      if (this.visAt(m.x, m.z) === 2) {
        this.memory.delete(id);
        continue;
      }
      this.pushBuilding(m.kind, m.owner, m.x, m.z, m.tier, m.built ? 1 : m.prog, -1, true, m.style);
    }

    this.renderLeaps(dt);
    this.updateMarkers(dt);

    // Corpses fall, then sink and fade.
    const keepC: Corpse[] = [];
    for (const c of this.corpses) {
      const age = this.time - c.t;
      if (age > 7) continue;
      keepC.push(c);
      this.unitLayer(c.kind, c.civ).push(c.x, c.y, c.z, c.yaw, unitScale(KINDS[c.kind].unit!), c.team, 0, AnimMode.Dying, c.t, 1);
    }
    this.corpses = keepC;
    const keepR: Rubble[] = [];
    for (const r of this.rubble) {
      const age = this.time - r.t;
      if (age > 40) continue;
      keepR.push(r);
      this.rubbleLayer.push(r.x, r.y - Math.max(0, age - 30) * 0.02, r.z, 0, r.scale, this.tmpColor.set(0xffffff), 0, AnimMode.Static, 0, 0);
    }
    this.rubble = keepR;
    this.collapsing = this.collapsing.filter((c) => {
      const f = 1 - (this.time - c.t) / 1.6;
      if (f <= 0) return false;
      this.pushBuilding(c.kind, c.owner, c.x, c.z, c.tier, f * f, -1, false);
      if (Math.random() < 0.3) this.particles.burst('dust', c.x + (Math.random() - 0.5) * 2, this.heights.at(c.x, c.z) + 0.3, c.z + (Math.random() - 0.5) * 2, 2, 0.8, 0.4);
      return true;
    });

    for (const l of this.unitLayers.values()) l.end();
    for (const l of this.buildingLayers.values()) l.inst.end();
    for (const l of this.resourceLayers.values()) l.end();
    for (const l of this.scaffoldLayers.values()) l.end();
    this.rubbleLayer.end();
    this.ringMesh.count = rings;
    this.ringMesh.instanceMatrix.needsUpdate = true;
    if (this.blobMesh) {
      this.blobMesh.count = this.blobs;
      this.blobMesh.instanceMatrix.needsUpdate = true;
      this.blobShape!.needsUpdate = true;
    }
    if (this.ringMesh.instanceColor) this.ringMesh.instanceColor.needsUpdate = true;
    this.barMesh.count = bars;
    this.barMesh.instanceMatrix.needsUpdate = true;
    this.barAttr.needsUpdate = true;

    this.renderProjectiles(alpha);
    this.renderPlacement(placement);
    this.renderRally(rallyOf);
    this.particles.update(dt);

    this.renderer.render(this.scene, cam);
    const info = this.renderer.info;
    const g = this.settings;
    this.stats = {
      drawCalls: info.render.calls, triangles: info.render.triangles, renderMs: performance.now() - t0, geometries: info.memory.geometries, textures: info.memory.textures,
      passes: g.shadows > 0 ? 2 : 1,
      effects: [g.antialias ? 'MSAA' : 'no AA', g.shadows > 0 ? `shadows ${[0, 1024, 2048, 4096][g.shadows]}` : 'no shadows', g.contactShadows ? 'contact' : '', g.glow ? 'glow' : ''].filter(Boolean).join(' · '),
    };
  }

  private pushBuilding(kind: number, owner: number, x: number, z: number, tier: number, buildFrac: number, s: number, staged = true, style = owner): { inst: AnimatedInstances; vis: BuildingVisual; roof?: THREE.Vector3[] } {
    const p = this.world.players[style];
    const wall = (KINDS[kind].building!.classes & C.Wall) !== 0;
    const l = wall ? this.buildingLayer(kind, p.civ, 0, this.wallVariant(Math.floor(x), Math.floor(z), owner)) : this.buildingLayer(kind, p.civ, tier);
    const y = this.heights.at(x, z);
    // Construction shows in four clear stages (plus the footing) instead of creeping up every frame.
    const shown = buildFrac >= 1 ? 1 : staged ? Math.max(0.1, Math.floor(buildFrac * 4) / 4) : buildFrac;
    l.inst.push(x, y, z, 0, 1, this.teamColors[owner], s >= 0 ? (s * 0.53) % 6.28 : 0, AnimMode.Static, 0, l.vis.height, shown);
    return l;
  }

  private writeBar(arr: Float32Array, i: number, frac: number, owner: number, teamOnly = false): void {
    const c = owner >= 0 ? this.teamColors[owner] : this.tmpColor.set(0xffffff);
    if (teamOnly) {
      arr[i * 4] = frac;
      arr[i * 4 + 1] = c.r;
      arr[i * 4 + 2] = c.g;
      arr[i * 4 + 3] = c.b;
      return;
    }
    const health = frac > 0.5 ? [0.3, 0.85, 0.25] : frac > 0.25 ? [0.95, 0.8, 0.2] : [0.95, 0.25, 0.15];
    arr[i * 4] = frac;
    // Blend team colour into health colour for quick identification.
    arr[i * 4 + 1] = health[0] * 0.75 + c.r * 0.25;
    arr[i * 4 + 2] = health[1] * 0.75 + c.g * 0.25;
    arr[i * 4 + 3] = health[2] * 0.75 + c.b * 0.25;
  }

  private renderProjectiles(alpha: number): void {
    const p = this.world.projectiles;
    for (const m of this.projLayers.values()) m.count = 0;
    const mtx = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const pos = new THREE.Vector3();
    const dir = new THREE.Vector3();
    const one = new THREE.Vector3(1, 1, 1);
    const fwd = new THREE.Vector3(0, 0, 1);
    for (let i = 0; i < MAX_PROJECTILES; i++) {
      if (!p.alive[i]) continue;
      const def = KINDS[p.kind[i]].unit?.attack ?? KINDS[p.kind[i]].building?.attack;
      const kind = def?.projectile ?? 'arrow';
      const f = Math.min(1, (p.t[i] + alpha) / p.total[i]);
      const sx = p.sx[i] * INV;
      const sz = p.sy[i] * INV;
      const tx = p.tx[i] * INV;
      const tz = p.ty[i] * INV;
      const dist = Math.hypot(tx - sx, tz - sz);
      const arc = kind === 'bullet' ? 0 : kind === 'ball' || kind === 'shell' ? dist * 0.08 : dist * (kind === 'stone' ? 0.3 : 0.18);
      const h0 = this.heights.at(sx, sz) + (KINDS[p.kind[i]].building ? 1.8 : 0.5);
      const h1 = this.heights.at(tx, tz) + 0.35;
      const x = sx + (tx - sx) * f;
      const z = sz + (tz - sz) * f;
      if (this.visAt(x, z) !== 2) continue;
      const y = h0 + (h1 - h0) * f + arc * 4 * f * (1 - f);
      const dy = (h1 - h0) + arc * 4 * (1 - 2 * f);
      dir.set(tx - sx, dy, tz - sz).normalize();
      q.setFromUnitVectors(fwd, dir);
      mtx.compose(pos.set(x, y, z), q, one);
      const m = this.projLayer(kind);
      if (m.count < 512) m.setMatrixAt(m.count++, mtx);
      if ((kind === 'ball' || kind === 'shell' || kind === 'stone') && Math.random() < 0.6) this.particles.burst('smoke', x, y, z, 1, 0.05, 0.1);
    }
    for (const m of this.projLayers.values()) m.instanceMatrix.needsUpdate = true;
  }

  /** Advance and draw leaping fish: a short arc out of the water with a splash on re-entry. */
  private renderLeaps(dt: number): void {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const one = new THREE.Vector3(1, 1, 1);
    const pos = new THREE.Vector3();
    let n = 0;
    this.leaps = this.leaps.filter((l) => {
      l.t += dt;
      const f = l.t / l.dur;
      if (f >= 1) return false;
      const dist = 0.9 * f;
      const h = 0.55 * 4 * f * (1 - f);
      // Nose up on the way out, nose down on the way back in.
      e.set(-(1 - 2 * f) * 1.1, Math.atan2(l.dx, l.dz), 0, 'YXZ');
      q.setFromEuler(e);
      pos.set(l.x + l.dx * dist, WATER_Y + h, l.z + l.dz * dist);
      m.compose(pos, q, one);
      this.leapMesh.setMatrixAt(n++, m);
      if (f > 0.85 && !l.splashed) {
        l.splashed = true;
        this.particles.burst('splash', pos.x, WATER_Y + 0.02, pos.z, 5, 0.5, 0.45);
      }
      return true;
    });
    this.leapMesh.count = n;
    this.leapMesh.instanceMatrix.needsUpdate = true;
  }

  private markers: { mesh: THREE.Mesh; t: number }[] = [];
  private markerGeo = new THREE.RingGeometry(0.78, 1, 40).rotateX(-Math.PI / 2);

  /** Order confirmation: a bold ring that shrinks to nothing where the order was given. */
  orderMarker(x: number, z: number, color = 0xffc21a): void {
    let m = this.markers.find((k) => k.t >= 0.6);
    if (!m) {
      const mesh = new THREE.Mesh(this.markerGeo, new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, fog: false }));
      mesh.renderOrder = 7;
      this.scene.add(mesh);
      m = { mesh, t: 1 };
      this.markers.push(m);
    }
    m.t = 0;
    (m.mesh.material as THREE.MeshBasicMaterial).color.set(color);
    m.mesh.position.set(x, Math.max(this.heights.at(x, z), WATER_Y) + 0.08, z);
    m.mesh.visible = true;
  }

  private updateMarkers(dt: number): void {
    for (const m of this.markers) {
      if (m.t >= 0.6) {
        m.mesh.visible = false;
        continue;
      }
      m.t += dt;
      const f = Math.min(1, m.t / 0.6);
      const r = 1.3 * (1 - f) * (1 - f) + 0.08;
      m.mesh.scale.set(r, 1, r);
      (m.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - f * 0.6;
    }
  }

  private wallGhosts: THREE.Mesh[] = [];
  private wallGhostGeo = new Map<string, THREE.BufferGeometry>();
  private ghostBad = new THREE.MeshBasicMaterial({ color: 0xff4040, transparent: true, opacity: 0.45, depthWrite: false });
  private ghostGood = new THREE.MeshBasicMaterial({ color: 0x50ff70, transparent: true, opacity: 0.45, depthWrite: false });

  /** Compile shaders for everything in the scene up front, so the first frames of a match do not stall. */
  warmUp(): void {
    try {
      this.renderer.compile(this.scene, this.cam.camera);
    } catch {
      /* compilation happens lazily on first draw instead */
    }
  }

  /** Ghost preview of a planned wall line; pieces join to each other and to existing walls. */
  setWallPreview(pieces: { kind: number; tx: number; ty: number; valid: boolean }[] | null): void {
    const list = pieces ?? [];
    const at = new Map<number, number>();
    for (const p of list) at.set(p.ty * this.world.size + p.tx, p.kind);
    const owner = Math.max(0, this.viewer);
    const civ = this.world.players[owner].civ;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      const variant = this.wallVariant(p.tx, p.ty, owner, (x, y) => at.get(y * this.world.size + x) ?? -1);
      const key = `${p.kind}:${civ}:${variant}`;
      let geo = this.wallGhostGeo.get(key);
      if (!geo) {
        geo = buildBuildingGeometry(KINDS[p.kind].building!, CIVS[civ], 0, variant).geometry;
        this.wallGhostGeo.set(key, geo);
      }
      let m = this.wallGhosts[i];
      if (!m) {
        m = new THREE.Mesh(geo, this.ghostGood);
        m.renderOrder = 6;
        this.scene.add(m);
        this.wallGhosts.push(m);
      }
      m.geometry = geo;
      m.material = p.valid ? this.ghostGood : this.ghostBad;
      const cx = p.tx + 0.5;
      const cz = p.ty + 0.5;
      m.position.set(cx, this.heights.at(cx, cz), cz);
      m.visible = true;
    }
    for (let i = list.length; i < this.wallGhosts.length; i++) this.wallGhosts[i].visible = false;
  }

  private renderPlacement(pl: { kind: number; tx: number; ty: number; valid: boolean } | null): void {
    if (!pl) {
      if (this.ghost) this.ghost.visible = false;
      this.footprint.visible = false;
      return;
    }
    const b = KINDS[pl.kind].building!;
    const civ = this.world.players[Math.max(0, this.viewer)].civ;
    const tier = ageTier(this.world.players[Math.max(0, this.viewer)].age);
    const key = `${pl.kind}:${civ}:${tier}`;
    if (this.ghostKey !== key) {
      if (this.ghost) this.scene.remove(this.ghost);
      const vis = this.buildingLayer(pl.kind, civ, tier, 0, true).vis;
      this.ghost = new THREE.Mesh(vis.geometry, this.ghostMat);
      this.ghost.renderOrder = 6;
      this.scene.add(this.ghost);
      this.ghostKey = key;
    }
    const cx = pl.tx + b.size / 2;
    const cz = pl.ty + b.size / 2;
    const y = this.heights.at(cx, cz);
    this.ghost!.visible = true;
    this.ghost!.position.set(cx, y, cz);
    this.ghostMat.color.set(pl.valid ? 0x50ff70 : 0xff4040);
    this.ghostMat.emissive.copy(this.ghostMat.color);
    this.ghostMat.opacity = 0.5 + 0.08 * Math.sin(this.time * 5);
    this.footprint.visible = true;
    this.footprint.position.set(cx, y + 0.08, cz);
    this.footprint.scale.set(b.size, 1, b.size);
    (this.footprint.material as THREE.MeshBasicMaterial).color.set(pl.valid ? 0x40ff60 : 0xff3030);
  }

  /** Strategic control points (territorial victory): monument coloured by controlling team. */
  setControlPoints(points: { x: number; y: number; team: number }[]): void {
    while (this.pointMeshes.length < points.length) {
      const g = new THREE.Group();
      const stone = new THREE.MeshStandardMaterial({ color: 0xb8b0a0, roughness: 0.9 });
      const base = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.3, 0.25, 16), stone);
      base.position.y = 0.12;
      const obelisk = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.25, 2.2, 4), stone);
      obelisk.position.y = 1.3;
      const flag = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.35, 0.03), new THREE.MeshStandardMaterial({ color: 0xffffff }));
      flag.position.set(0.32, 2.2, 0);
      const ring = new THREE.Mesh(new THREE.RingGeometry(2.8, 3, 48).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5, depthWrite: false }));
      ring.position.y = 0.06;
      base.castShadow = obelisk.castShadow = true;
      g.add(base, obelisk, flag, ring);
      this.scene.add(g);
      this.pointMeshes.push(g);
    }
    points.forEach((p, i) => {
      const g = this.pointMeshes[i];
      const x = p.x * INV;
      const z = p.y * INV;
      g.position.set(x, this.heights.at(x, z), z);
      const owner = this.world.players.find((pl) => pl.team === p.team);
      const col = owner ? this.teamColors[owner.id] : new THREE.Color(0xdddddd);
      ((g.children[2] as THREE.Mesh).material as THREE.MeshStandardMaterial).color.copy(col);
      ((g.children[3] as THREE.Mesh).material as THREE.MeshBasicMaterial).color.copy(col);
      (g.children[2] as THREE.Mesh).rotation.y = Math.sin(this.time * 2 + i) * 0.4;
    });
  }

  private renderRally(slot: number): void {
    const w = this.world;
    if (slot < 0 || !w.alive[slot] || w.cat[slot] !== Cat.Building) {
      this.rally.visible = false;
      return;
    }
    const t = w.resolve(w.rallyTarget[slot]);
    const x = (t >= 0 ? w.x[t] : w.rallyX[slot]) * INV;
    const z = (t >= 0 ? w.y[t] : w.rallyY[slot]) * INV;
    this.rally.visible = true;
    this.rally.position.set(x, Math.max(this.heights.at(x, z), WATER_Y), z);
    const cloth = this.rally.children[0].children[1] as THREE.Mesh;
    (cloth.material as THREE.MeshStandardMaterial).color.copy(this.teamColors[w.owner[slot]]);
    cloth.rotation.y = Math.sin(this.time * 3) * 0.3;
  }

  // ---------- Picking ----------
  /** Ray-march the heightfield to find the ground point under a screen position (NDC). */
  groundAtNdc(nx: number, ny: number): THREE.Vector3 | null {
    const cam = this.cam.camera;
    const o = cam.position.clone();
    const d = new THREE.Vector3(nx, ny, 0.5).unproject(cam).sub(o).normalize();
    const size = this.world.size;
    let prev = 0;
    for (let t = 0; t < 400; t += 0.25) {
      const x = o.x + d.x * t;
      const y = o.y + d.y * t;
      const z = o.z + d.z * t;
      const h = Math.max(-0.12, this.heights.at(x, z));
      if (y <= h) {
        // Refine with bisection.
        let a = prev;
        let b = t;
        for (let i = 0; i < 8; i++) {
          const m = (a + b) / 2;
          const yy = o.y + d.y * m;
          if (yy <= Math.max(-0.12, this.heights.at(o.x + d.x * m, o.z + d.z * m))) b = m;
          else a = m;
        }
        const p = o.clone().addScaledVector(d, b);
        p.x = Math.min(size - 0.01, Math.max(0, p.x));
        p.z = Math.min(size - 0.01, Math.max(0, p.z));
        return p;
      }
      prev = t;
      if (y < -5) break;
    }
    // Fall back to plane intersection.
    if (d.y >= 0) return null;
    const t = -o.y / d.y;
    const p = o.clone().addScaledVector(d, t);
    p.x = Math.min(size - 0.01, Math.max(0, p.x));
    p.z = Math.min(size - 0.01, Math.max(0, p.z));
    return p;
  }

  /** Project an entity to screen pixels. */
  screenPos(s: number, width: number, height: number, out: THREE.Vector3): THREE.Vector3 {
    const w = this.world;
    const x = w.x[s] * INV;
    const z = w.y[s] * INV;
    const k = KINDS[w.kind[s]];
    const lift = k.building ? 0.6 : k.unit ? (k.unit.classes & C.Cavalry ? 0.5 : 0.35) : 0.3;
    out.set(x, Math.max(this.heights.at(x, z), WATER_Y) + lift, z).project(this.cam.camera);
    out.x = (out.x * 0.5 + 0.5) * width;
    out.y = (-out.y * 0.5 + 0.5) * height;
    return out;
  }

  /** Pick the entity under a screen point among visible slots. */
  pick(px: number, py: number, width: number, height: number): number {
    const w = this.world;
    const v = new THREE.Vector3();
    let best = -1;
    let bestD = Infinity;
    const pxPerUnit = height / (2 * Math.tan((this.cam.camera.fov * Math.PI) / 360) * this.cam.distance);
    for (const s of this.visibleSlots) {
      if (!w.alive[s]) continue;
      this.screenPos(s, width, height, v);
      const k = KINDS[w.kind[s]];
      const r = (k.building ? k.building.size * 0.55 : k.unit ? Math.max(0.35, k.unit.radius * 1.6) : 0.45) * pxPerUnit;
      const dx = v.x - px;
      const dy = v.y - py;
      const d2 = dx * dx + dy * dy;
      // Units take priority over buildings and resources at the same distance.
      const pri = k.unit ? 0 : k.building ? 1 : 2;
      if (d2 < r * r && d2 + pri * 50 < bestD) {
        bestD = d2 + pri * 50;
        best = s;
      }
    }
    return best;
  }

  heightAtFx(x: number, y: number): number {
    return this.world.heightAt(x, y) / HEIGHT_SCALE;
  }

  dispose(): void {
    this.models.dispose();
    this.renderer.dispose();
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
    });
  }
}
